import { AppBoot } from "@/app/components/AppBoot";
import { AuthNavigator } from "@/app/components/AuthNavigator";
import { AuthProvider } from "@/app/providers/AuthContext";
import { supabase } from "@/shared/api";
import { LanguageProvider } from "@/shared/contexts/LanguageContext";
import { setupAuthLanguageSync } from "@/shared/lib/authLanguageSync";
import { isNetworkError } from "@/shared/lib/errorUtils";
import { captureException } from "@/shared/lib/sentry";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { ReactNode, useEffect, useMemo, Component, ErrorInfo } from "react";
import { Outlet, useLocation, useNavigation } from "react-router-dom";

/**
 * Simple single QueryClient setup.
 * One stable QueryClient for the entire app with Supabase RLS handling practice filtering.
 */
const createQueryClient = () => {
  const client = new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Skip logging expected status errors that are not true errors
        const message = error?.message || '';
        const isExpectedStatus =
          message === 'RequiresOnboarding' ||
          message === 'SessionRefreshFailed';

        // Transient network failures are connectivity faults, not app bugs.
        // The error reaching here is the normalized one; useSupabaseQuery
        // preserves the raw fetch error on `originalError`, so classify against
        // that to catch it reliably.
        const originalError = (error as { originalError?: unknown })?.originalError;
        const isNetworkFault = isNetworkError(originalError ?? error);

        if (!isExpectedStatus && !isNetworkFault) {
          // Capture unexpected errors in Sentry
          captureException(error, {
            source: "QueryCache",
            queryKey: JSON.stringify(query?.queryKey),
          });

          if (import.meta.env.DEV) {
            console.error('[ReactQuery] Query error', {
              queryKey: query?.queryKey,
              error,
            });
          }
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // Sentry capture happens in useSupabaseMutation with the *original*
        // (untranslated) error; capturing here too would double-report each
        // failure with the generic translated message. Keep DEV logging only.
        if (import.meta.env.DEV) {
          console.error('[ReactQuery] Mutation error', {
            mutationKey: mutation?.options?.mutationKey,
            error,
          });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 10 * 60_000,
        refetchOnWindowFocus: true, // Enable built-in window focus refetching
        retry: (failureCount, error) => {
          // Don't retry on auth errors
          if (error?.message?.includes('JWT') || error?.message?.includes('auth')) {
            return false;
          }
          return failureCount < 2;
        },
      },
    },
  });

  if (import.meta.env.DEV) {
    console.log('[Root] Created single stable QueryClient', { 
      timestamp: new Date().toISOString()
    });
  }

  return client;
};

// Removed QueryReadinessContext - no longer needed with always-mounted QueryClientProvider
// Queries should gate on auth/practice readiness via enabled prop, not QueryClient readiness

// Memoized styles and handlers for performance
const errorContainerStyle = {
  padding: "20px",
  textAlign: "center" as const,
  color: "#ef4444",
  backgroundColor: "#fef2f2",
  border: "1px solid #fecaca",
  borderRadius: "8px",
  margin: "20px",
};

const errorButtonStyle = {
  padding: "8px 16px",
  backgroundColor: "#ef4444",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
};

const handleRefresh = () => window.location.reload();

// QueryClientErrorBoundary to catch and log QueryClient-related errors
class QueryClientErrorBoundary extends Component<
  { children: ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): { hasError: boolean } {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("[QueryClientErrorBoundary] Caught error:", error);
    console.error("[QueryClientErrorBoundary] Error info:", errorInfo);

    // Capture error in Sentry
    captureException(error, {
      source: "QueryClientErrorBoundary",
      componentStack: errorInfo.componentStack,
    });

    // In development, you might want to show more details
    if (import.meta.env.DEV) {
      console.error(
        "[QueryClientErrorBoundary] Component stack:",
        errorInfo.componentStack
      );
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div
          style={errorContainerStyle}
        >
          <h2>Something went wrong with the query system</h2>
          <p>
            Please refresh the page. If the problem persists, contact support.
          </p>
          <button
            onClick={handleRefresh}
            style={errorButtonStyle}
          >
            Refresh Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}


export const Root = () => {
  const location = useLocation();
  const navigation = useNavigation();
  
  useEffect(() => {
    setupAuthLanguageSync();
  }, []);

  // Minimal router diagnostics to avoid destabilizing providers
  useEffect(() => {
    if (import.meta.env.DEV && navigation.state !== "idle") {
      console.log("[ROOT ROUTER DEBUG] Navigation", {
        path: location.pathname,
        state: navigation.state,
      });
    }
  }, [location.pathname, navigation.state]);

  // Create single stable QueryClient for the entire app
  const queryClient = useMemo(() => {
    const client = createQueryClient();
    if (import.meta.env.DEV) {
      console.log("[Root] Created single QueryClient for entire app");
    }
    return client;
  }, []);

  // Enhanced window visibility logging to track auth/query state
  useEffect(() => {
    let previousVisibilityState = document.visibilityState;
    
    const onVis = async () => {
      if (import.meta.env.DEV) {
        const currentState = document.visibilityState;
        const { data } = await supabase.auth.getSession();
        console.log("[Window] visibility change", {
          from: previousVisibilityState,
          to: currentState,
          timestamp: new Date().toISOString(),
          timeSinceChange: Date.now(),
          activeQueries: queryClient.getQueryCache().getAll().length,
          cacheSize: queryClient.getQueryCache().getAll().length,
          online: navigator.onLine,
          hasSession: !!data.session,
          sessionExpiresAt: data.session?.expires_at || 'none',
        });
        previousVisibilityState = currentState;
      }
    };
    const onOffline = () => {
      if (import.meta.env.DEV) {
        console.log("[Window] offline");
      }
    };
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("offline", onOffline);
    return () => {
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("offline", onOffline);
    };
  }, [queryClient]);

  return (
    <LanguageProvider>
      <QueryClientErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <AppBoot>
              <AuthNavigator />
              <Outlet />
            </AppBoot>
          </AuthProvider>
        </QueryClientProvider>
      </QueryClientErrorBoundary>
    </LanguageProvider>
  );
};
